/*  UI  */
import { Box, Flex } from '@auviknetworks/iceberg-ui';

/*  Shared  */
import { PageTitle } from 'application/Shared/Components/App';
import { SessionHelper } from 'application/Shared/Helpers';

/* Hooks */
import { useSession } from 'hooks/redux/Selectors/session';

/*  Packages  */
import MuiMarkdown from 'mui-markdown';
import React from 'react';

import { About } from './About';
import {
    DashboardContextProvider,
    useDashboardContext
} from './DashboardContext';
import { templateInterpolation } from './Helpers/templateInterpolation';
import { Settings } from './Settings';
import { Workspace } from './Workspace/Workspace';
import {
    type Dashboard as DashboardType,
    type GroupPanel as GroupPanelType,
    type LayoutItem
} from './types';

/**
 * Displays the dashboard with the title, description, settings and workspace.
 */
export const DashboardComponent = () => {
    const session = useSession();
    const sessionHelper = new SessionHelper(session);
    const {
        dashboardState: { isEditing },
        schema: dashboard,
        context: dashboardContext
    } = useDashboardContext();

    // Key components are showned by default.
    const {
        title = true,
        description = true,
        settings = true
    } = dashboard.meta ?? {};

    return (
        <Flex
            data-testid="dashboardComponent"
            className="dashboard-root"
            flexDirection="column"
            gap={2}
            width="100%"
        >
            <Flex
                justifyContent="space-between"
                alignItems="center"
                data-testid="dashboardComponentPageTitleSettings"
            >
                {title && (
                    <Flex
                        justifyContent="flex-start"
                        alignItems="center"
                        data-testid="dashboardComponentPageTitle"
                    >
                        <PageTitle
                            title={templateInterpolation(
                                dashboard.title,
                                dashboardContext.variables
                            )}
                        />
                    </Flex>
                )}
                {(settings || sessionHelper.hasPermission(['super_admin'])) && (
                    <Flex
                        justifyContent="flex-end"
                        alignItems="center"
                        data-testid="dashboardComponentPageSettings"
                        width="100%"
                    >
                        <Settings />
                    </Flex>
                )}
            </Flex>

            {description && dashboard.description && (
                <Flex
                    marginBottom="16px"
                    data-testid="dashboardComponentDescription"
                >
                    <MuiMarkdown>
                        {dashboard.description &&
                            templateInterpolation(
                                dashboard.description,
                                dashboardContext.variables
                            )}
                    </MuiMarkdown>
                </Flex>
            )}
            {/* When editing, we need to set a min width to avoid layout issues */}
            <Box
                data-testid="dashboardComponentWorkspaceWrapperOverflow"
                sx={{ overflowX: 'auto', overflowY: 'hidden' }}
            >
                <Box
                    data-testid="dashboardComponentWorkspaceWrapper"
                    minWidth={isEditing ? '1200px' : 'auto'}
                >
                    <Workspace schema={dashboard} />
                </Box>
            </Box>
        </Flex>
    );
};

interface DashboardProps {
    dashboard: DashboardType;
    isEditing?: boolean;
    setSchema?: React.Dispatch<
        React.SetStateAction<DashboardType | GroupPanelType>
    >;
    handleLayoutChange?: React.Dispatch<React.SetStateAction<LayoutItem[]>>;
}

/**
 * Wraps the dashboard component with the context provider.
 */
export const Dashboard = ({
    dashboard,
    isEditing,
    setSchema,
    handleLayoutChange
}: DashboardProps) => (
    <DashboardContextProvider
        handleLayoutChange={handleLayoutChange}
        setSchema={setSchema}
        isEditing={isEditing}
        dashboard={dashboard}
    >
        <DashboardComponent />
        <About />
    </DashboardContextProvider>
);
